import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { sha256 } from 'js-sha256';
import { v4 as uuidv4 } from 'uuid';
import { Router } from '@angular/router';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { LocalStorageService } from '../local/local-storage.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private _http: HttpClient,
    private _localStorage: LocalStorageService,
    private _router: Router
  ) {}

  private _generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async _generateCodeChallenge(verifier: string): Promise<string> {
    // Prefer Web Crypto (requires secure context: https or localhost)
    let hashBytes: Uint8Array;
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const data = new TextEncoder().encode(verifier);
      const digest = await crypto.subtle.digest('SHA-256', data);
      hashBytes = new Uint8Array(digest);
    } else {
      // Fallback for non-secure contexts where SubtleCrypto is unavailable.
      const buffer = sha256.arrayBuffer(verifier);
      hashBytes = new Uint8Array(buffer);
    }
    const base64 = btoa(String.fromCharCode(...Array.from(hashBytes)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  public async initiateMicrosoftLogin() {
    // Generate PKCE verifier & challenge client-side
    const verifier = this._generateCodeVerifier();
    const challenge = await this._generateCodeChallenge(verifier);
    // Use secure-context randomUUID when available, else fallback to uuidv4
    const state = (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function')
      ? (crypto as any).randomUUID()
      : uuidv4();
    sessionStorage.setItem('pkce_verifier', verifier);
    sessionStorage.setItem('oauth_state', state);
    // Build authorize URL locally (tenant and client id expected in env served via index.html meta or config)
    // For simplicity here we hardcode redirect origin (assumes same origin as app) and rely on backend env for client id via global var if needed
    const tenant = environment.azure.tenant;
    const clientId = environment.azure.clientId;
    const redirectUri = encodeURIComponent(environment.azure.redirectUri);
    const scope = encodeURIComponent('openid profile email offline_access');
    const authUrl = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&response_mode=query&scope=${scope}&state=${state}&code_challenge=${challenge}&code_challenge_method=S256`;
    window.location.href = authUrl;
  }

  public exchangeCode(code: string, state: string) {
    const verifier = sessionStorage.getItem('pkce_verifier');
    const storedState = sessionStorage.getItem('oauth_state');
    if (!verifier || !storedState || storedState !== state) {
      console.error('Invalid or missing PKCE verifier/state');
      return;
    }
    this._http
      .post('auth/microsoft/callback', { code, state, code_verifier: verifier })
      .subscribe({
        next: (res: any) => {
          console.debug('[AuthService] token exchange response', res);
          sessionStorage.removeItem('pkce_verifier');
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_code_redeemed');
          if (res.refresh_token) {
            this._localStorage.setItem('refresh_token', res.refresh_token);
          }
          this._localStorage.setItem('token', res.access_token);
          const userWithProvider = { ...res.user, provider: 'microsoft' };
          this._localStorage.setItem('user', JSON.stringify(userWithProvider));
          const postLoginPath = (environment as any).postLoginPath || '/';
          if (postLoginPath.startsWith('http')) {
            window.location.href = postLoginPath;
          } else {
            this._router.navigate([postLoginPath]);
          }
        },
        error: (error: any) => {
          console.error('OAuth code exchange failed', error);
        },
      });
  }

  public refreshToken = (): Observable<any> => {
    return this._http
      .post('auth/microsoft/refresh-token', {
        refresh_token: this._localStorage.getItem('refresh_token'),
      })
      .pipe(
        tap((res: any) => {
          this._localStorage.setItem('token', res.access_token);
          this._localStorage.setItem('refresh_token', res.refresh_token);
        }),
        catchError((error: any) => {
          console.error(error);
          return throwError(error);
        })
      );
  };

  public logout = () => {
    this._localStorage.removeItem('token');
    this._localStorage.removeItem('refresh_token');
    this._localStorage.removeItem('user');
    this._router.navigate(['/login']);
  };

  public isLoggedIn = () => {
    return !!this._localStorage.getItem('token');
  };

  public getTokenFromLocalStorage = () => {
    return this._localStorage.getItem('token');
  };

  public getUserFromLocalStorage = () => {
    const user = this._localStorage.getItem('user');
    if (user) {
      return JSON.parse(user);
    }
  };
}
